import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../../components/UI/button";
import images from "../../../constants/images";
import { CheckMarkIcon, LoaderVectors } from "../../../constants/icons";
import { useSystem } from "../../../hooks/use-system";
import callAPI from "../../../api";
import { defaultLanguage } from "../../../constants/languages";
import { isIOS } from "react-device-detect";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/UI/form";
import { Input } from "../../../components/UI/input";
import {
  isValidPhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";
import { handleApiReturn } from "../../../helpers/logic";

const FormSchema = z.object({
  phonenumber: z
    .string()
    .min(8, { message: "" })
    .regex(/^\+?[0-9]+$/, { message: "" })
    .refine((value) => isValidPhoneNumber(value), {
      message: "",
    }),
});

export function InputForm({
  popUpText,
  config,
  language,
  setStep,
  handleVerificationRequest,
}) {
  const [countrySelected, setCountrySelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const system = useSystem();
  const form = useForm({
    resolver: zodResolver(FormSchema),
    mode: "onChange",
    defaultValues: {
      phonenumber: countrySelected,
    },
  });

  const isTesting = config.is_testing;

  useEffect(() => {
    if (config.country_code) {
      const country = getCountryCallingCode(config.country_code?.toUpperCase());
      setCountrySelected(country);
      form.setValue("phonenumber", `+${country}`);
    }
  }, [config.country_code, form]);

  var _supportsLocalStorage =
    !!window.localStorage &&
    typeof localStorage.getItem === "function" &&
    typeof localStorage.setItem === "function" &&
    typeof localStorage.removeItem === "function";

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (value.phonenumber) setError("");

      if (!value.phonenumber.startsWith(`+${countrySelected}`)) {
        form.setValue("phonenumber", `+${countrySelected}`);
      }
      if (value.phonenumber?.length <= countrySelected.length) {
        form.setValue("phonenumber", `+${countrySelected}`);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, form, countrySelected]);

  function onSubmit(data) {
    const phone = form.getValues("phonenumber");
    const isPhoneValid = isValidPhoneNumber(phone);
    if (!isPhoneValid) {
      setError("Please enter a valid number");
      return;
    }
    if (loading) return;

    setLoading(true);

    if (_supportsLocalStorage) {
      window.localStorage.setItem("phone", phone);
    }
    if (isTesting) {
      setStep((prev) => prev + 1);

      setTimeout(() => {
        setStep((prev) => prev + 1);
      }, 3000);
    } else {
      callAPI(
        {
          phone_number: phone,
          country_code: config.country_code,
          path: window.location.pathname,
          carrier_sub_code: null,
          session_id: localStorage.getItem("nj_sessionId"),
          token: localStorage.getItem("nj_token"),
          supportStorage: _supportsLocalStorage,
          web_click_id: system.webClickId,
          config_id: system.config.public_id,
          qs: window.location.search,
          language: system.language,
          af_token: config.antifraud_token,
        },
        "initSubscription"
      )
        .then(async (data) => {
          if (config.flow !== "CLICK_2_SMS_REDIRECT") {
            setLoading(false);
          }
          if (data.status !== "success") return;

          await handleApiReturn(config, data.payload, () =>
            setStep((prev) => prev + 1)
          );
        })
        .catch((e) => {
          setLoading(false);
        });
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col justify-center items-center gap-6 mt-6"
      >
        {popUpText && (
          <div className="relative bg-[#FFCC00] rounded-lg px-3 py-1 w-full">
            <p
              className="text-sm text-center font-semibold"
              dangerouslySetInnerHTML={{ __html: popUpText }}
            ></p>
            <img
              className="pt-[0.5px] absolute left-1/2 -translate-x-1/2"
              src={images.downArrow}
              alt="Yellow Down Arrow"
            />
          </div>
        )}
        <FormField
          control={form.control}
          name="phonenumber"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  iconShow={form.formState.isValid}
                  icon={
                    <div
                      className={`${
                        loading ? "hidden" : ""
                      } bg-[#04D36B] p-1 flex justify-center items-center w-6 h-6 rounded-full`}
                    >
                      <CheckMarkIcon fill={"#FFFFFF"} />
                    </div>
                  }
                  className={`${
                    !form.formState.isValid
                      ? `bg-white`
                      : "bg-transparent border-gray-500"
                  } text-xl`}
                  disabled={loading}
                  placeholder="Enter your phone number"
                  {...field}
                />
              </FormControl>

              <FormMessage />

              {error && <p className="text-sm font-medium">{error}</p>}
            </FormItem>
          )}
        />

        <Button
          className={`flex flex-row gap-2 px-5 py-4 mt-2 ${
            !form.formState.isValid ? "cursor-not-allowed " : ""
          } ${
            loading ? "cursor-not-allowed " : "cursor-pointer "
          }  font-semibold text-lg md:text-2xl rounded-full tracking-wider`}
          style={{
            backgroundColor: config.button_primary || "#e02f1fe3",
            color: config.button_text_color || "#000",
          }}
          type="submit"
          disabled={!form.formState.isValid || loading}
        >
          {loading ? (
            <>
              <span className="animate-spin">
                <LoaderVectors />
              </span>
              <div className="flex flex-row justify-end items-center">
                <p className="m-0">Checking</p>
                <span className="animate-bounce">.</span>
                <span className="animate-bounce delay-200">.</span>
                <span className="animate-bounce delay-300">.</span>
              </div>
            </>
          ) : (
            config.texts?.[language || defaultLanguage]?.[0]?.button_text || ""
          )}
        </Button>
        <div className="flex justify-center items-center w-full">
          <span className="text-sm">
            {config.texts?.[language || defaultLanguage]?.[0]?.button_caption ||
              ""}
          </span>
        </div>
      </form>
    </Form>
  );
}
